/**
 * Population Structure API Service
 *
 * SWR hooks for fetching detailed population data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { PopulationStructure } from '../../types/modules'

/**
 * Fetch population structure for a given year
 *
 * GET /population/structure?year={year}
 */
export function usePopulationStructure(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<PopulationStructure>(
    [`/population/structure`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 5 * 60 * 1000, // 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch population data for specific district
 *
 * GET /population/by-district/{districtId}?year={year}
 */
export function usePopulationByDistrict(districtId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<PopulationStructure>(
    districtId ? [`/population/by-district/${districtId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch age group distribution across districts
 *
 * GET /population/by-age-group/{groupId}?year={year}
 */
export function usePopulationByAgeGroup(groupId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR(
    groupId ? [`/population/by-age-group/${groupId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch multi-year population trends
 *
 * GET /population/trends?district_id={id}&age_group_id={id}
 */
export function usePopulationTrends(filters?: {
  districtId?: number
  ageGroupId?: number
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.ageGroupId) {
    params.age_group_id = filters.ageGroupId.toString()
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: typeof params
    trends: Array<{
      year: number
      total_population: string
      male_count: string
      female_count: string
    }>
  }>(
    [`/population/trends`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch age groups (reference data)
 *
 * GET /age-groups
 */
export function useAgeGroups() {
  const { data, error, isLoading } = useSWR(
    '/age-groups',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 60 * 60 * 1000, // Cache for 1 hour
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}
